export const detachFileType = (fileName: string) => {
  const lastDot = fileName.lastIndexOf('.');

  if (lastDot === -1) {
    return {
      fileName,
      fileType: '',
    };
  }

  return {
    fileName: fileName.substring(0, lastDot),
    fileType: fileName.substring(lastDot),
  };
};

export const convertFileSizeToMB = (fileSizeInBytes: number): string => {
  if (fileSizeInBytes === undefined) {
    return '0';
  }
  if (fileSizeInBytes === 0) {
    return '0';
  }
  const fileSizeInMB = fileSizeInBytes / (1024 * 1024);
  return fileSizeInMB.toFixed(2);
};

export const getFileExtension = (fileName: string): string => {
  try {
    const splitFileName = fileName.split('.');
    if (splitFileName.length <= 1) return '';

    return splitFileName.pop()?.toLowerCase() ?? '';
  } catch {
    return '';
  }
};

export const cloneWithNewFileName = (
  originalFile: File,
  newFileName: string,
) => {
  return new File([originalFile], newFileName, {
    type: originalFile.type,
  });
};

export const sanitizeFileName = (originalName: string): string => {
  // file's name should valid below pattern to create a valid gsutil URI
  const allowedChars = /^[-a-zA-Z0-9@_~.+]*$/;

  // Quickly check if the entire name is valid
  if (allowedChars.test(originalName)) {
    return originalName;
  }

  const sanitizedName = originalName.replace(/[^-a-zA-Z0-9@_~.+]/g, '');

  return sanitizedName.trim();
};

// return a promise that resolves with a File instance
// Check the solution here: https://stackoverflow.com/questions/35940290/how-to-convert-base64-string-to-javascript-file-object-like-as-from-file-input-f
export async function urlToFile(
  url: string,
  filename: string,
  mimeType?: string,
): Promise<File> {
  // A utility function to get file extension based on the MIME type
  const getFileExtensionFromMimeType = (mimeType: string): string => {
    switch (mimeType) {
      case 'image/jpeg':
        return 'jpeg';
      case 'image/png':
        return 'png';
      case 'image/gif':
        return 'gif';
      case 'image/webp':
        return 'webp';
      default:
        return '';
    }
  };

  // Append the correct file extension to the filename if it's not present
  const ensureFilenameHasExtension = (
    filename: string,
    mimeType: string,
  ): string => {
    const extension = getFileExtensionFromMimeType(mimeType);
    if (!filename.includes(`.${extension}`)) {
      return `${filename}.${extension}`;
    }
    return filename;
  };

  if (url.startsWith('data:')) {
    const arr = url.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || mimeType;
    const bstr = atob(arr[arr.length - 1]);
    const n = bstr.length;
    const u8arr = new Uint8Array(n);

    for (let i = 0; i < n; i++) {
      u8arr[i] = bstr.charCodeAt(i);
    }

    const fileWithExtension = ensureFilenameHasExtension(
      filename,
      mime || mimeType || '',
    );
    const file = new File([u8arr], fileWithExtension, {
      type: mime || mimeType,
    });
    return file;
  }

  const response = await fetch(url);
  const arrayBuffer = await response.arrayBuffer();

  const fileWithExtension = ensureFilenameHasExtension(
    filename,
    mimeType || '',
  );
  return new File([arrayBuffer], fileWithExtension, { type: mimeType });
}
